import { ChevronDown, ChevronRight, ExternalLink, Shield } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import type { DiscriminatedCoverageCreatedEvent } from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface CoverageCreatedEventProps {
  event: DiscriminatedCoverageCreatedEvent;
  matterId: string;
}

export const CoverageCreatedEvent: React.FC<CoverageCreatedEventProps> = ({
  event,
  matterId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { payload, occurred_at, user, agent } = event;
  const { coverage, needs_approval } = payload;

  // Get carrier name
  const carrierName = coverage.carrier?.name || 'Unknown Carrier';

  // Get policy type
  const policyType = coverage.policy_type
    ? formatPolicyType(coverage.policy_type)
    : null;

  // Get coverage category
  const category = coverage.category
    ? formatCoverageCategory(coverage.category)
    : null;

  // Get named insured
  const namedInsured = coverage.named_insured?.join(', ');

  // Get policy identifiers
  const policyNumbers = coverage.policy_identifiers
    ?.map((pi) => pi.identifier_value)
    .filter(Boolean);

  // Count additional details
  const hasAdjusters = coverage.adjusters && coverage.adjusters.length > 0;
  const hasClaims = coverage.claims && coverage.claims.length > 0;
  const hasVehicles = coverage.vehicles && coverage.vehicles.length > 0;
  const hasPolicyDetails =
    coverage.policy_details && coverage.policy_details.length > 0;
  const hasBenefitsWithLimits = coverage.benefits?.some(
    (b) => b.limitations && Object.keys(b.limitations).length > 0,
  );
  const hasAdditionalDetails =
    hasAdjusters || hasClaims || hasVehicles || hasPolicyDetails || hasBenefitsWithLimits;

  return (
    <EventContainer>
      <EventHeader
        icon={<Shield size={20} />}
        title="Coverage Created"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* Carrier and Policy Type */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <span className="font-medium">{carrierName}</span>
            {policyType && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{policyType}</Badge>
              </>
            )}
            {category && (
              <>
                <span className="text-slate-400">•</span>
                <Badge variant="outline">{category}</Badge>
              </>
            )}
            {needs_approval && <Badge variant="outline">Needs Approval</Badge>}
          </div>

          {/* Named Insured */}
          {namedInsured && (
            <div className="text-xs">
              <span className="text-slate-600">Insured:</span>{' '}
              <span>{namedInsured}</span>
            </div>
          )}

          {/* Policy Numbers */}
          {policyNumbers && policyNumbers.length > 0 && (
            <div className="text-xs">
              <span className="text-slate-600">Policy Numbers:</span>{' '}
              <span className="font-mono text-[11px]">
                {policyNumbers.join(', ')}
              </span>
            </div>
          )}

          {/* Benefits */}
          {coverage.benefits && coverage.benefits.length > 0 && (
            <div className="text-xs">
              <span className="text-slate-600">Benefits:</span>{' '}
              <span className="text-[11px]">
                {coverage.benefits
                  .map((b) => {
                    const label = formatBenefitType(b.benefit_type);
                    if (b.limitations) {
                      const values = Object.values(b.limitations);
                      if (values.length > 0) {
                        return `${label} (${values.map(formatLimitCompact).join('/')})`;
                      }
                    }
                    return label;
                  })
                  .join(', ')}
              </span>
            </div>
          )}

          {/* Collapsible Additional Details */}
          {hasAdditionalDetails && (
            <Collapsible open={isOpen} onOpenChange={setIsOpen}>
              <CollapsibleTrigger className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-800 mt-1.5">
                {isOpen ? (
                  <ChevronDown size={12} />
                ) : (
                  <ChevronRight size={12} />
                )}
                <span>{isOpen ? 'Hide' : 'Show'} additional details</span>
              </CollapsibleTrigger>

              <CollapsibleContent className="mt-2 space-y-2.5 border-l-2 border-slate-200 pl-2.5">
                {/* Benefits & Limits */}
                {hasBenefitsWithLimits && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Benefits & Limits
                    </div>
                    <div className="space-y-1">
                      {coverage.benefits
                        ?.filter(
                          (b) =>
                            b.limitations &&
                            Object.keys(b.limitations).length > 0,
                        )
                        .map((benefit, index) => (
                          <div key={index} className="text-[11px]">
                            <div className="font-medium">
                              {formatBenefitType(benefit.benefit_type)}
                            </div>
                            {benefit.limitations &&
                              Object.entries(benefit.limitations).map(
                                ([key, value]) => (
                                  <div
                                    key={key}
                                    className="text-slate-600 ml-2"
                                  >
                                    {key}: {formatLimitCompact(value)}
                                  </div>
                                ),
                              )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Adjusters */}
                {hasAdjusters && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Adjusters
                    </div>
                    <div className="space-y-1.5">
                      {coverage.adjusters?.map((adjuster, index) => (
                        <div key={index} className="text-[11px] space-y-0.5">
                          {adjuster.name && (
                            <div className="font-medium">{adjuster.name}</div>
                          )}
                          {adjuster.benefit_type && (
                            <div className="text-slate-600">
                              Benefit:{' '}
                              {formatBenefitType(adjuster.benefit_type)}
                            </div>
                          )}
                          {adjuster.handles && adjuster.handles.length > 0 && (
                            <div className="text-slate-600">
                              {adjuster.handles
                                .map((h) => h.value || '')
                                .filter(Boolean)
                                .join(', ')}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Claims */}
                {hasClaims && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Claims
                    </div>
                    <div className="space-y-1.5">
                      {coverage.claims?.map((claim, index) => (
                        <div key={index} className="text-[11px] space-y-0.5">
                          {claim.claim_identifier && (
                            <div>
                              Claim #:{' '}
                              <span className="font-mono">
                                {claim.claim_identifier}
                              </span>
                            </div>
                          )}
                          {claim.benefit_type && (
                            <div className="text-slate-600">
                              Benefit: {formatBenefitType(claim.benefit_type)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Vehicles */}
                {hasVehicles && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Vehicles
                    </div>
                    <div className="space-y-1.5">
                      {coverage.vehicles?.map((vehicle, index) => (
                        <div key={index} className="text-[11px] space-y-0.5">
                          <div className="font-medium">
                            {[vehicle.year, vehicle.make, vehicle.model]
                              .filter(Boolean)
                              .join(' ') || 'Vehicle'}
                          </div>
                          {vehicle.vin && (
                            <div className="text-slate-600">
                              VIN:{' '}
                              <span className="font-mono">{vehicle.vin}</span>
                            </div>
                          )}
                          {vehicle.license_plate && (
                            <div className="text-slate-600">
                              Plate: {vehicle.license_plate}
                            </div>
                          )}
                          {vehicle.color && (
                            <div className="text-slate-600">
                              Color: {vehicle.color}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Policy Details */}
                {hasPolicyDetails && (
                  <div>
                    <div className="text-[11px] font-medium text-slate-700 mb-0.5">
                      Policy Details
                    </div>
                    <div className="space-y-0.5">
                      {coverage.policy_details?.map((detail, index) => (
                        <div key={index} className="text-[11px] flex gap-2">
                          <span className="text-slate-600 min-w-[100px]">
                            {detail.key}:
                          </span>
                          <span className="flex-1">{detail.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </EventBody>

      <EventActions>
        <a
          href={`/matters/${matterId}/coverage/${coverage.id}`}
          className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
        >
          View Coverage
          <ExternalLink size={11} />
        </a>
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatPolicyType(type: string): string {
  const types: Record<string, string> = {
    AUTO: 'Auto',
    HOMEOWNERS: 'Homeowners',
    COMMERCIAL: 'Commercial',
    HEALTH: 'Health',
    UMBRELLA: 'Umbrella',
    OTHER: 'Other',
  };
  return types[type] || type;
}

function formatCoverageCategory(category: string): string {
  const categories: Record<string, string> = {
    FIRST_PARTY: '1st Party',
    FIRST_PARTY_HEALTH: '1st Party Health',
    THIRD_PARTY: '3rd Party',
    UNCLEAR: 'Unclear',
  };
  return categories[category] || category;
}

function formatLimitCompact(value: number): string {
  if (value >= 1000000) {
    const num = value / 1000000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}M`;
  }
  if (value >= 1000) {
    const num = value / 1000;
    return `$${num % 1 === 0 ? num.toFixed(0) : parseFloat(num.toFixed(2))}K`;
  }
  return `$${value.toLocaleString()}`;
}

function formatBenefitType(type: string | null | undefined): string {
  if (!type) return '';

  const benefits: Record<string, string> = {
    PERSONAL_INJURY_PROTECTION: 'PIP',
    MEDICAL_PAYMENTS: 'Med Pay',
    UNINSURED_MOTORIST: 'UM',
    UNDER_INSURED_MOTORIST: 'UIM',
    BODILY_INJURY_LIABILITY: 'BI',
    PROPERTY_DAMAGE_LIABILITY: 'PD',
    COMPREHENSIVE_AUTO: 'Comprehensive',
    COLLISION_AUTO: 'Collision',
    OTHER: 'Other',
  };
  return benefits[type] || type;
}
